import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Box,
  Stack,
  Button,
  SubmitButton,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { FileEditIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import useSendDocumentsForElectronicSignature from '../../hooks/useSendDocumentsForElectronicSignature';

const CompleteDocumentModal = ({
  open,
  onClose,
  onSaveAndComplete,
  onSendForSignature,
  intl,
  isSubmitting = false,
  hasPortal = false,
  messages,
}) => {
  const { isEnabled } = useSendDocumentsForElectronicSignature();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={intl.formatMessage(
        messages?.completeDocumentTitle || {
          id: 'App.CompleteDocument.Title',
          defaultMessage: 'Complete Document',
        },
      )}
      subtitle={intl.formatMessage(
        messages?.completeDocumentSubtitle || {
          id: 'App.CompleteDocument.Subtitle',
          defaultMessage: 'Send for signature',
        },
      )}
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant={isEnabled && hasPortal ? 'primarySubtle' : 'primary'}
            onClick={onSaveAndComplete}
            disabled={isSubmitting}
          >
            {intl.formatMessage(
              messages?.saveAndCompleteButton || {
                id: 'App.CompleteDocument.SaveAndComplete',
                defaultMessage: 'Save & Complete',
              },
            )}
          </Button>
          {isEnabled && hasPortal && (
            <SubmitButton
              onClick={onSendForSignature}
              disabled={isSubmitting}
              isSubmitting={isSubmitting}
              startIcon={<FileEditIcon />}
              variant="primary"
            >
              {intl.formatMessage(
                messages?.sendForSignatureButton || {
                  id: 'App.CompleteDocument.SendForSignature',
                  defaultMessage: 'Send for Signature',
                },
              )}
            </SubmitButton>
          )}
        </>
      }
      ModalProps={{
        PaperProps: {
          sx: {
            width: {
              xs: 'calc(100% - 32px)',
              sm: 600,
            },
            maxHeight: 'calc(100vh - 100px)',
          },
        },
      }}
    >
      <Box>
        {isEnabled && hasPortal ? (
          <Stack spacing={3}>
            {/* Send for Signature Section - Only show if hasPortal */}
            {hasPortal && (
              <Box>
                <Typography variant="titleSmall" sx={{ mb: 1 }}>
                  {intl.formatMessage(
                    messages?.sendForSignatureButton || {
                      id: 'App.CompleteDocument.SendForSignature',
                      defaultMessage: 'Send for Signature',
                    },
                  )}
                </Typography>
                <Typography
                  variant="bodyMedium"
                  color="text.secondary"
                  component="div"
                >
                  {intl.formatMessage(
                    messages?.completeDocumentDescription || {
                      id: 'App.CompleteDocument.Description',
                      defaultMessage:
                        "Send the form to the applicant's portal for electronic signing.",
                    },
                  )}
                </Typography>
              </Box>
            )}

            {/* Save & Complete Section */}
            <Box>
              <Typography variant="titleSmall" sx={{ mb: 1 }}>
                {intl.formatMessage(
                  messages?.saveAndCompleteButton || {
                    id: 'App.CompleteDocument.SaveAndComplete',
                    defaultMessage: 'Save & Complete',
                  },
                )}
              </Typography>
              <Typography
                variant="bodyMedium"
                color="text.secondary"
                component="div"
              >
                {intl.formatMessage(
                  messages?.saveAndCompleteDescription || {
                    id: 'App.CompleteDocument.SaveAndCompleteDescription',
                    defaultMessage:
                      'This will finalize the document and save your changes without sending it for signature.',
                  },
                )}
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Stack spacing={3}>
            <Typography variant="bodyMedium" color="text.secondary">
              {intl.formatMessage(
                messages?.completeConfirmation || {
                  id: 'App.CompleteDocument.CompleteConfirmation',
                  defaultMessage:
                    'Are you sure you want to complete this application?',
                },
              )}
            </Typography>
          </Stack>
        )}
      </Box>
    </Modal>
  );
};

CompleteDocumentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaveAndComplete: PropTypes.func.isRequired,
  onSendForSignature: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  isSubmitting: PropTypes.bool,
  hasPortal: PropTypes.bool,
  messages: PropTypes.object.isRequired,
};

export default CompleteDocumentModal;
